/**
 * Форматирование чисел, дат и счётных форм.
 *
 * Живёт в общем пакете намеренно: приложение, бот и API показывают одни и те
 * же суммы и сроки, и две реализации форматирования однажды разойдутся — а
 * человек увидит другую сумму или другую дату там, где решает, платить ли.
 *
 * Всё три — через Intl. Это то, что docs/i18n/README.md называет нерешаемым
 * простой подстановкой строк.
 */

export type Lang = 'ru' | 'en';

/** Знаков после запятой у родной валюты. Она у сделки одна. */
const TON_DECIMALS = 9n;

/**
 * Как валюта подписана на экране.
 *
 * Монета сети переименована: Toncoin стал Gram, тикер TON — GRAM. Сеть при
 * этом осталась The Open Network, адреса и контракты не менялись, так что
 * переименование целиком живёт здесь — одной строкой, а не тридцатью
 * литералами по экранам, как было с прежней валютой.
 *
 * Слово «TON» в текстах остаётся там, где речь о сети и о TON Connect: это
 * не валюта, и переименование их не касается.
 */
export const CURRENCY = 'GRAM';

/** Тикер чужой площадки: ту же монету она всё ещё зовёт по-старому. */
export function ticker(currency: string | null | undefined): string {
  return currency == null || currency === 'TON' || currency === 'GRAM' ? CURRENCY : currency;
}

/**
 * Сумма из нанотонов в текст локали: 1 000,00 против 1,000.00
 *
 * От двух знаков до четырёх, и лишние нули не показываются: 175,00, но
 * 0,095. Двух хватало, пока долг считался в валюте с ценами в сотни: там
 * сотая доля — копейка. В родной валюте оффер бывает на одну монету, и
 * два знака начинают врать на глазах — доля продавца со взноса 0,095
 * показывалась как «0,09», доля с платежа 0,855 как «0,85», а итог рядом
 * честно говорил 0,95. Человек видел, что 0,09 и 0,85 не дают 0,95, и был
 * прав: складывать нечего, обрезаны были слагаемые.
 *
 * Отсюда же округление вместо обрезания: обрезание всегда занижает, и на
 * мелких суммах это заметно.
 */
export function formatMoney(nano: bigint, lang: Lang): string {
  // Деление в числе с плавающей точкой безопасно: наши суммы — это монеты,
  // а не нанотоны на пределе, и до потери точности (9 миллионов монет)
  // здесь бесконечно далеко.
  const value = Number(nano) / Number(10n ** TON_DECIMALS);
  return new Intl.NumberFormat(lang === 'ru' ? 'ru-RU' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

/**
 * Цена чужой площадки: любая валюта, любая точность.
 *
 * Отдельно от `formatMoney`: у чужой площадки бывает своя валюта и своя
 * точность, и считать её нанотонами значит соврать человеку о цене
 * ровно там, где он решает, сколько предлагать.
 */
export function formatAmount(value: bigint, decimals: number, lang: Lang): string {
  const scale = 10n ** BigInt(decimals);
  const whole = value / scale;
  const rest = ((value % scale) * 100n) / scale;
  const shown = Number(whole) + Number(rest) / 100;
  return new Intl.NumberFormat(lang === 'ru' ? 'ru-RU' : 'en-US', {
    minimumFractionDigits: shown < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(shown);
}

/**
 * Дата с названием месяца. Короткое числовое «07.03» здесь запрещено: русский
 * читатель поймёт его как 7 марта, англоязычный — как 3 июля.
 */
export function formatDateTime(unix: number, lang: Lang): string {
  const date = new Date(unix * 1000);
  const locale = lang === 'ru' ? 'ru-RU' : 'en-GB';
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(date);
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(date);
  return `${day}, ${time} UTC`;
}

const UNITS: Record<Lang, Record<'day' | 'hour', Record<string, string>>> = {
  ru: {
    day: { one: 'день', few: 'дня', many: 'дней', other: 'дня' },
    hour: { one: 'час', few: 'часа', many: 'часов', other: 'часа' },
  },
  en: {
    day: { one: 'day', other: 'days' },
    hour: { one: 'hour', other: 'hours' },
  },
};

function plural(n: number, unit: 'day' | 'hour', lang: Lang): string {
  const rules = new Intl.PluralRules(lang === 'ru' ? 'ru-RU' : 'en-US');
  const form = rules.select(n);
  const table = UNITS[lang][unit];
  return `${n} ${table[form] ?? table['other'] ?? ''}`;
}

/**
 * «2 дня 14 часов» / «2 days 14 hours».
 *
 * В русском три формы (день / дня / дней), в английском две. Конкатенацией это
 * не решается, поэтому форму выбирает Intl.PluralRules, а не наша логика.
 */
export function formatDuration(seconds: number, lang: Lang): string {
  const total = Math.max(0, seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);

  if (days === 0 && hours === 0) {
    return lang === 'ru' ? 'меньше часа' : 'less than an hour';
  }
  const parts: string[] = [];
  if (days > 0) parts.push(plural(days, 'day', lang));
  if (hours > 0) parts.push(plural(hours, 'hour', lang));
  return parts.join(' ');
}

